import { createPersistedStore } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import type { ServiceDetailsResponse, SrPricingInfo } from '../types';

/**
 * Service Details Store State Interface
 * Manages service details data fetched from useServiceDetailsQuery
 */
interface ServiceDetailsState extends BaseStoreState {
  serviceDetails: ServiceDetailsResponse | null;
  setServiceDetails: (_serviceDetails: ServiceDetailsResponse | null) => void;
  clearServiceDetails: () => void;
  reset: () => void;

  // Derived getters for specific data
  getPricingData: () => SrPricingInfo | null;
  getProposalDocumentId: () => string | null;
  getContractDocumentId: () => string | null;
  getServiceId: () => string | null;
  getSrAttemptId: () => string | null;
  getServiceTierId: () => string | null;
  getRitmSysId: () => string | null;
}

const initialState: Omit<ServiceDetailsState, keyof BaseStoreState> = {
  serviceDetails: null,
  setServiceDetails: () => {},
  clearServiceDetails: () => {},
  reset: () => {},
  getPricingData: () => null,
  getProposalDocumentId: () => null,
  getContractDocumentId: () => null,
  getServiceId: () => null,
  getSrAttemptId: () => null,
  getServiceTierId: () => null,
  getRitmSysId: () => null,
};

/**
 * Service Details Store
 * Persisted store for managing service details across components
 * Used to share serviceDetails data between RequestedServiceContainer and ProposalReviewLayout
 */
export const useServiceDetailsStore = createPersistedStore<ServiceDetailsState>(
  'service-details',
  initialState,
  (set, get) => ({
    setServiceDetails: (serviceDetails: ServiceDetailsResponse | null) => {
      set({ serviceDetails } as Partial<ServiceDetailsState>);
    },

    clearServiceDetails: () => {
      set({ serviceDetails: null } as Partial<ServiceDetailsState>);
    },

    reset: () => {
      set({
        serviceDetails: null,
        loading: false,
        error: null,
      } as Partial<ServiceDetailsState>);
    },

    // Derived getter for pricing data
    getPricingData: () => {
      const { serviceDetails } = get();
      return serviceDetails?.pricing ?? null;
    },

    // Derived getter for proposal document ID
    getProposalDocumentId: () => {
      const { serviceDetails } = get();
      return serviceDetails?.proposalDocumentId ?? null;
    },

    // Derived getter for contract document ID
    getContractDocumentId: () => {
      const { serviceDetails } = get();
      return serviceDetails?.contractDocumentId ?? null;
    },

    // Derived getter for service ID
    getServiceId: () => {
      const { serviceDetails } = get();
      return serviceDetails?.serviceId ?? null;
    },

    // Derived getter for SR attempt ID
    getSrAttemptId: () => {
      const { serviceDetails } = get();
      return serviceDetails?.srAttemptId ?? null;
    },

    // Derived getter for service tier sys ID
    getServiceTierId: () => {
      const { serviceDetails } = get();
      return serviceDetails?.serviceTierId ?? null;
    },

    // Derived getter for the parent service RITM sys ID
    getRitmSysId: () => {
      const { serviceDetails } = get();
      return serviceDetails?.ritmSysId ?? null;
    },
  }),
  {
    partialize: (state) => ({
      serviceDetails: state.serviceDetails,
    }),
  }
);

// Selectors for convenient access
export const useServiceDetailsData = () =>
  useServiceDetailsStore((state) => state.serviceDetails);

export const useServicePricing = () =>
  useServiceDetailsStore((state) => state.getPricingData());

export const useProposalDocumentId = () =>
  useServiceDetailsStore((state) => state.getProposalDocumentId());

export const useContractDocumentId = () =>
  useServiceDetailsStore((state) => state.getContractDocumentId());

export const useServiceId = () =>
  useServiceDetailsStore((state) => state.getServiceId());

export const useSrAttemptId = () =>
  useServiceDetailsStore((state) => state.getSrAttemptId());

export const useServiceTierId = () =>
  useServiceDetailsStore((state) => state.getServiceTierId());

export const useRitmSysId = () =>
  useServiceDetailsStore((state) => state.getRitmSysId());

export const useServiceDetailsLoading = () =>
  useServiceDetailsStore((state) => state.loading);

export const useServiceDetailsError = () =>
  useServiceDetailsStore((state) => state.error);
